import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Select, Typography } from 'antd';
import {
  AFS_GROUP,
  ALTERNATIVES_GROUP,
  AssetGroupAllocationPayload,
  BONDS_GROUP,
  CASH_APPROACH,
  CASH_GROUP,
  CDS_GROUP,
  createUpdateGroupAllocations,
  CRYPTO_GROUP,
  EQUITIES_GROUP,
  FUTURES_GROUP,
  FX_GROUP,
  GROUP_ASSET_NAME,
  HTM_GROUP,
  OPTIONS_GROUP,
  PORTFOLIO_MANAGEMENT,
  REPO_GROUP,
  selectors,
  updateInvestmentProfile,
  WEALTH_MANAGEMENT,
} from 'state';
import SettingsCard from '../../SessionSettingsShared/components/SettingsCard';
import { INVESTMENT_PROFILE_LABELS } from '../../SessionSettingsShared/helpers';
import InlineNumber from './InlineNumber';
import './inline_edit.css';

type State = {
  [assetGroup: number]: AssetGroupAllocationPayload;
};

/** Same percent display as the v1 AllocationsPanel: store 0–1 fractions, show ×100. */
const pctFormatter = (value: string | number | undefined): string => {
  const nr = Number(value) || 0;
  return `${Math.round(nr * 100)}%`;
};
const pctParser = (value: string | undefined): number => {
  const parsedValue = `${value?.replaceAll('%', '')}`;
  const nr = Number(parsedValue);
  if (!isNaN(nr)) {
    return Number((nr / 100).toFixed(2));
  }
  return 0;
};

/**
 * Seamless v2 of the Strategic Allocations panel. Same wiring as
 * AllocationsPanel (reads session allocations + the eight has*Group selectors,
 * dispatches createUpdateGroupAllocations with the full set / updateInvestmentProfile),
 * but with NO edit mode: every band cell is an always-mounted InlineNumber that
 * commits on blur/Enter, and the wealth-mode profile Select applies instantly.
 * Per-row validation (lower < upper) keeps the bad value in place with an error
 * flag and skips the dispatch until it's fixed; nothing reflows on toggle.
 */
const AllocationsCardV2: React.FC = () => {
  const dispatch = useDispatch();
  const {
    id,
    asset_group_allocations,
    risk_approach,
    management_type,
    investment_profile,
  } = useSelector(selectors.activeSession);

  const buildLocal = (): State => {
    const next: State = {};
    asset_group_allocations.forEach(a => {
      next[a.asset_group] = {
        id: a.id,
        asset_group: a.asset_group,
        lower_limit: a.lower_limit,
        upper_limit: a.upper_limit,
      };
    });
    return next;
  };
  // Initialise from the session so the first paint shows real values (no flash).
  const [local, setLocal] = useState<State>(buildLocal);
  // Per-asset-group cell error flags (true when lower >= upper on commit).
  const [errors, setErrors] = useState<{ [assetGroup: number]: boolean }>({});

  const hasEquitiesGroup = useSelector(selectors.hasEquitiesGroup());
  const hasBondsGroup = useSelector(selectors.hasBondsGroup());
  const hasAlternativesGroup = useSelector(selectors.hasAlternativesGroup());
  const hasCryptoGroup = useSelector(selectors.hasCryptoGroup());
  const hasHTMGroup = useSelector(selectors.hasHTMGroup());
  const hasAFSGroup = useSelector(selectors.hasAFSGroup());
  const hasRepo = useSelector(selectors.hasRepoGroup());
  const hasCDS = useSelector(selectors.hasCDSGroup());
  const hasFutures = useSelector(selectors.hasFuturesGroup());
  const hasSimplifiedOptions = useSelector(
    selectors.hasSimplifiedOptionsGroup(),
  );

  const assetGroups = { ...GROUP_ASSET_NAME };
  // FX no longer taken into consideration in allocations table
  delete assetGroups[FX_GROUP];
  if (
    risk_approach !== CASH_APPROACH ||
    management_type === WEALTH_MANAGEMENT
  ) {
    delete assetGroups[CASH_GROUP];
  }
  if (!hasEquitiesGroup) {
    delete assetGroups[EQUITIES_GROUP];
  }
  if (!hasBondsGroup) {
    delete assetGroups[BONDS_GROUP];
  }
  if (!hasAlternativesGroup) {
    delete assetGroups[ALTERNATIVES_GROUP];
  }
  if (!hasCryptoGroup) {
    delete assetGroups[CRYPTO_GROUP];
  }
  if (!hasHTMGroup) {
    delete assetGroups[HTM_GROUP];
  }
  if (!hasAFSGroup) {
    delete assetGroups[AFS_GROUP];
  }
  if (!hasRepo) {
    delete assetGroups[REPO_GROUP];
  }
  if (!hasCDS) {
    delete assetGroups[CDS_GROUP];
  }
  if (!hasFutures) {
    delete assetGroups[FUTURES_GROUP];
  }
  // The group name is shared with legacy options, but this standard allocation row belongs only to class 19.
  if (!hasSimplifiedOptions) {
    delete assetGroups[OPTIONS_GROUP];
  }

  useEffect(() => {
    setLocal(buildLocal());
    setErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset_group_allocations]);

  const rows = Object.entries(assetGroups).map(([key, name]) => ({
    assetGroup: parseInt(key, 10),
    name: String(name),
  }));

  const commit = (
    assetGroup: number,
    field: 'lower_limit' | 'upper_limit',
    value: number,
  ) => {
    const cur = local[assetGroup];
    const nextAlloc: AssetGroupAllocationPayload = {
      ...cur,
      asset_group: assetGroup,
      [field]: value,
    };
    const next: State = { ...local, [assetGroup]: nextAlloc };
    setLocal(next);

    // Per-row validation: when both bounds are present they must be ascending.
    const lower = nextAlloc.lower_limit;
    const upper = nextAlloc.upper_limit;
    const bothPresent =
      lower !== null &&
      lower !== undefined &&
      upper !== null &&
      upper !== undefined;
    if (bothPresent && lower >= upper) {
      // Keep the bad value so the user can fix it, flag the row, skip dispatch.
      setErrors(prev => ({ ...prev, [assetGroup]: true }));
      return;
    }
    setErrors(prev => ({ ...prev, [assetGroup]: false }));
    dispatch(
      createUpdateGroupAllocations({ allocations: Object.values(next) }),
    );
  };

  const isPortfolio = management_type === PORTFOLIO_MANAGEMENT;

  // Wealth-management mode: investment-profile selector, applies instantly.
  if (!isPortfolio) {
    return (
      <SettingsCard title="Target Strategic Allocations">
        <div className="ie-formrow">
          <span className="ie-formrow__label">Investment profile</span>
          <Select
            value={investment_profile}
            onChange={(val: number) =>
              dispatch(
                updateInvestmentProfile({
                  session_id: id,
                  investment_profile: val,
                }),
              )
            }
            style={{ width: 220 }}
          >
            {Object.entries(INVESTMENT_PROFILE_LABELS).map(([val, label]) => (
              <Select.Option key={val} value={Number(val)}>
                {label}
              </Select.Option>
            ))}
          </Select>
        </div>
      </SettingsCard>
    );
  }

  if (rows.length === 0) {
    return (
      <SettingsCard title="Target Strategic Allocations">
        <div className="set-studio__placeholder" style={{ minHeight: 80 }}>
          <Typography.Text type="secondary">
            Select and save some instruments.
          </Typography.Text>
        </div>
      </SettingsCard>
    );
  }

  // Portfolio-management mode: seamless bands table.
  return (
    <SettingsCard title="Target Strategic Allocations">
      <table className="ie-table">
        <colgroup>
          <col style={{ width: '52%' }} />
          <col style={{ width: '24%' }} />
          <col style={{ width: '24%' }} />
        </colgroup>
        <thead>
          <tr>
            <th>Asset Class Group</th>
            <th className="ie-col-num">Lower</th>
            <th className="ie-col-num">Upper</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => {
            const alloc = local[r.assetGroup];
            const rowError = !!errors[r.assetGroup];
            return (
              <tr key={r.assetGroup}>
                <td className="ie-row-label">{r.name}</td>
                <td className="ie-col-num">
                  <InlineNumber
                    value={alloc?.lower_limit ?? 0}
                    onCommit={v => commit(r.assetGroup, 'lower_limit', v)}
                    error={rowError}
                    formatter={pctFormatter}
                    parser={pctParser}
                    step={0.01}
                    min={0}
                    max={1}
                    ariaLabel={`${r.name} lower limit`}
                  />
                </td>
                <td className="ie-col-num">
                  <InlineNumber
                    value={alloc?.upper_limit ?? 0}
                    onCommit={v => commit(r.assetGroup, 'upper_limit', v)}
                    error={rowError}
                    formatter={pctFormatter}
                    parser={pctParser}
                    step={0.01}
                    min={0}
                    max={1}
                    ariaLabel={`${r.name} upper limit`}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </SettingsCard>
  );
};

export default AllocationsCardV2;
